import { bodycamClient } from "./clients";
import type { TranscriptData } from "../shared/interfaces/TranscriptData";

interface TranslateTranscriptsResponse {
  targetLanguageCode: string;
  transcripts: Record<string, TranscriptData>;
}

// Transcripts are immutable, so translations can be cached for the session
// (e.g. downloading the same transcript as PDF then DOCX in one language).
const translationCache = new Map<string, TranscriptData>();

const cacheKey = (evidenceId: string, languageCode: string) =>
  `${evidenceId}:${languageCode}`;

/**
 * @name translateTranscripts
 * @description Translates the transcripts of the given evidence items into the
 * target language. Evidence IDs without a transcript are omitted from the
 * returned map. Throws on request failure.
 * @param {string[]} evidenceIds - The evidence IDs whose transcripts to translate
 * @param {string} targetLanguageCode - ISO language code (e.g. "es")
 * @returns {Promise<Record<string, TranscriptData>>} Translated transcripts keyed by evidence ID
 */
export const translateTranscripts = async (
  evidenceIds: string[],
  targetLanguageCode: string,
): Promise<Record<string, TranscriptData>> => {
  const result: Record<string, TranscriptData> = {};
  const missingIds: string[] = [];

  for (const id of evidenceIds) {
    const cached = translationCache.get(cacheKey(id, targetLanguageCode));
    if (cached) {
      result[id] = cached;
    } else {
      missingIds.push(id);
    }
  }

  if (missingIds.length > 0) {
    const response = await bodycamClient.post<TranslateTranscriptsResponse>(
      "/evidence/transcript/translate",
      { evidenceIds: missingIds, targetLanguageCode },
    );
    // Only take requested IDs so unexpected backend entries are never cached
    for (const id of missingIds) {
      const transcript = response.data.transcripts?.[id];
      if (transcript) {
        translationCache.set(cacheKey(id, targetLanguageCode), transcript);
        result[id] = transcript;
      }
    }
  }

  return result;
};
